import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import ctl from '@netlify/classnames-template-literals'
import { useState } from 'react'
import { Glyphs } from '../../glyphs'

export type WidgetComboboxOption<T extends string> = {
	id: T
	label: string
}

export type WidgetComboboxProps<T extends string> = {
	options: ReadonlyArray<WidgetComboboxOption<T>>
	value: T | ''
	onChange: (value: T) => void
	placeholder?: string
	loading?: boolean
	emptyMessage?: string
	filter?: (query: string, options: ReadonlyArray<WidgetComboboxOption<T>>) => ReadonlyArray<WidgetComboboxOption<T>>
}

function defaultFilter<T extends string>(
	query: string,
	options: ReadonlyArray<WidgetComboboxOption<T>>,
): ReadonlyArray<WidgetComboboxOption<T>> {
	if (!query) { return options }
	const q = query.toLowerCase()
	return options.filter((option) => option.label.toLowerCase().includes(q))
}

export function WidgetCombobox<T extends string>({
	options,
	value,
	onChange,
	placeholder,
	loading = false,
	emptyMessage = 'No results',
	filter = defaultFilter,
}: WidgetComboboxProps<T>) {
	const [query, setQuery] = useState('')
	const filtered = filter(query, options)
	const selectedLabel = options.find((o) => o.id === value)?.label

	return (
		<div className="text-sm text-grey-600 text-left">
			<Combobox
				value={value as string}
				onChange={(v: string) => {
					if (v) { onChange(v as T) }
				}}
				immediate
			>
				<div className="relative bg-white ring ring-grey-200 rounded-sm shadow-sm">
					<ComboboxInput
						placeholder={loading ? 'Loading...' : placeholder}
						displayValue={() => selectedLabel ?? ''}
						onChange={(e) => setQuery(e.target.value)}
						className={ctl(`
							text-left py-1 px-2 w-full bg-transparent text-sm outline-none placeholder-grey-400
							${loading && 'animate-pulse'}
						`)}
					/>
					<ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-1.5">
						{loading
							? (
								<span
									role="status"
									aria-label="Loading"
									className="inline-block size-3 rounded-full border-2 border-grey-200 border-t-grey-500 animate-spin"
								/>
							)
							: <Glyphs.micro.DownChevron className="scale-75" />}
					</ComboboxButton>
				</div>

				<ComboboxOptions
					anchor="bottom start"
					className="z-50 bg-white min-w-[calc(var(--input-width)+8px)] [--anchor-gap:8px] max-h-48 overflow-y-auto ring-1 py-1 ring-grey-100 shadow-md rounded text-sm text-grey-600 empty:hidden"
				>
					{loading && <div className="px-2 py-1 text-grey-400">Loading...</div>}
					{!loading && filtered.length === 0 && <div className="px-2 py-1 text-grey-400">{emptyMessage}</div>}
					{filtered.map((option) => (
						<ComboboxOption
							key={option.id}
							value={option.id}
							className={ctl(`
								relative group cursor-pointer
								data-[selected]:bg-blue-50 data-[selected]:text-blue-600
								data-[focus]:bg-blue-100
								mx-1 px-2 py-1 rounded pr-7
							`)}
						>
							{option.label}
							<div className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-500 opacity-0 group-data-[selected]:opacity-100">
								<Glyphs.heroiconsMicro.CheckIcon />
							</div>
						</ComboboxOption>
					))}
				</ComboboxOptions>
			</Combobox>
		</div>
	)
}
